import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRightLeft, Pencil, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";

// Phase 2 (Feb 2026) — Dynamic Taxonomy admin.
// Adds three capabilities on top of the previous toggle-only UI:
//   1. Create top-level Issue Types (non-builtin) with a high_risk flag.
//   2. Rename non-builtin Issue Types AND non-builtin Specific Issues.
//   3. Toggle the high_risk flag on any Issue Type (built-in or admin-added).
//   4. Hard-delete issue types + specific issues (builtin included) — guarded
//      by an active-case reference check the frontend surfaces before the
//      destructive confirm form is shown.
//   5. Move a specific issue under a different parent issue type. Historical
//      cases keep their snapshot; new/edited cases pick up the new parent.
//
// Backend endpoints already exist under /api/admin/taxonomy/*; this file
// is a pure client-side surface for those endpoints. Built-in row protection
// is enforced by the server: renames of built-ins are blocked (409), but
// delete + high_risk + active toggles are allowed regardless of the flag.

function DeleteTaxonomyDialog({
  open,
  onOpenChange,
  target,      // { kind: 'issue_type'|'specific_issue', id, name, parent_name? }
  onConfirm,
  onReassignAndDelete,  // async (targetId, reason, childrenAction) => void
  data,        // { issue_types, specific_issues } — used to build reassign target dropdown
}) {
  const [refs, setRefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  // Phase 2 (Feb 2026) — reassign-and-delete branch. Only offered when the
  // pre-flight reference check returns active_case_count > 0; otherwise the
  // simple delete flow remains.
  const [mode, setMode] = useState(null); // 'delete' | 'reassign'
  const [reassignTargetId, setReassignTargetId] = useState("");
  const [childrenAction, setChildrenAction] = useState("reparent");

  useEffect(() => {
    if (!open || !target) return;
    setRefs(null);
    setConfirmName("");
    setReason("");
    setMode(null);
    setReassignTargetId("");
    setChildrenAction("reparent");
    setLoading(true);
    const path =
      target.kind === "issue_type"
        ? `/admin/taxonomy/issue-types/${target.id}/references`
        : `/admin/taxonomy/specific-issues/${target.id}/references`;
    api
      .get(path)
      .then(({ data }) => {
        setRefs(data);
        // Auto-preselect the primary mode based on what the situation calls for.
        setMode(data.active_case_count > 0 ? "reassign" : "delete");
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        setRefs({ active_case_count: 0, sample_case_ids: [], error: true });
        setMode("delete");
      })
      .finally(() => setLoading(false));
  }, [open, target]);

  // Reassign target options (filtered to the same entity kind + active +
  // excluding the source row itself).
  const reassignOptions = React.useMemo(() => {
    if (!target || !data) return [];
    if (target.kind === "issue_type") {
      return (data.issue_types || []).filter(
        (t) => t.active && t.id !== target.id,
      );
    }
    return (data.specific_issues || []).filter(
      (s) => s.active && s.id !== target.id,
    );
  }, [target, data]);

  const canConfirmDelete =
    mode === "delete" &&
    !!refs &&
    refs.active_case_count === 0 &&
    confirmName.trim() === (target?.name || "") &&
    reason.trim().length >= 10;

  const canConfirmReassign =
    mode === "reassign" &&
    !!reassignTargetId &&
    reassignTargetId !== target?.id &&
    confirmName.trim() === (target?.name || "") &&
    reason.trim().length >= 10;

  const handle = async () => {
    if (mode === "reassign") {
      if (!canConfirmReassign) return;
      setSaving(true);
      try {
        await onReassignAndDelete(reassignTargetId, reason.trim(), childrenAction);
        onOpenChange(false);
      } catch {
        /* toast surfaced by handler */
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!canConfirmDelete) return;
    setSaving(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch {
      /* toast surfaced by onConfirm */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (saving ? null : onOpenChange(o))}>
      <DialogContent data-testid="taxonomy-delete-dialog" className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-[color:var(--danger-fg)]" />
            Delete {target?.kind === "issue_type" ? "issue type" : "specific issue"}?
          </DialogTitle>
          <DialogDescription>
            {target?.kind === "issue_type"
              ? `'${target?.name}' and all of its subcategories.`
              : `'${target?.name}' under '${target?.parent_name}'.`}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <p className="text-sm text-[color:var(--page-fg-muted)]">
            Checking active case references…
          </p>
        )}

        {!loading && refs && refs.active_case_count > 0 && (
          <div className="space-y-3">
            <div
              className="rounded-md border p-3 text-sm"
              style={{
                backgroundColor: "var(--danger-bg)",
                borderColor: "var(--danger-border)",
                color: "var(--page-fg)",
              }}
              data-testid="taxonomy-delete-blocked"
            >
              <p className="font-semibold">
                {refs.active_case_count} active case
                {refs.active_case_count === 1 ? "" : "s"} reference this row.
              </p>
              {refs.sample_case_ids?.length > 0 && (
                <p className="mt-1.5 font-mono text-[11px] text-[color:var(--page-fg-secondary)]">
                  Sample: {refs.sample_case_ids.join(", ")}
                  {refs.active_case_count > refs.sample_case_ids.length ? ", …" : ""}
                </p>
              )}
              <p className="mt-1.5 text-[12.5px]">
                Choose how to proceed:
              </p>
            </div>

            {/* Mode picker */}
            <div className="space-y-2">
              <label
                className="flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm"
                style={{
                  borderColor:
                    mode === "reassign" ? "var(--brand-accent)" : "var(--border-subtle)",
                  backgroundColor:
                    mode === "reassign" ? "var(--surface-2)" : "transparent",
                }}
              >
                <input
                  type="radio"
                  data-testid="taxonomy-delete-mode-reassign"
                  name="taxonomy-delete-mode"
                  value="reassign"
                  checked={mode === "reassign"}
                  onChange={() => setMode("reassign")}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium text-[color:var(--page-fg)]">
                    Reassign all {refs.active_case_count} case
                    {refs.active_case_count === 1 ? "" : "s"} and delete this{" "}
                    {target?.kind === "issue_type" ? "issue type" : "specific issue"}
                  </span>
                  <span className="mt-0.5 block text-[11.5px] text-[color:var(--page-fg-muted)]">
                    Sweeps active cases onto a target you pick, then hard-deletes the
                    source. Historical archived cases keep their snapshot.
                  </span>
                </span>
              </label>
              <label
                className="flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm"
                style={{
                  borderColor:
                    mode === "cancel" ? "var(--brand-accent)" : "var(--border-subtle)",
                }}
              >
                <input
                  type="radio"
                  data-testid="taxonomy-delete-mode-cancel"
                  name="taxonomy-delete-mode"
                  value="cancel"
                  checked={mode === "cancel"}
                  onChange={() => setMode("cancel")}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium text-[color:var(--page-fg)]">
                    Cancel — I&apos;ll reassign manually first
                  </span>
                  <span className="mt-0.5 block text-[11.5px] text-[color:var(--page-fg-muted)]">
                    Close this dialog. Update the affected cases individually or via
                    bulk import, then retry the delete.
                  </span>
                </span>
              </label>
            </div>

            {mode === "reassign" && (
              <div className="space-y-3 rounded-md border p-3" style={{ borderColor: "var(--border-subtle)" }}>
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: "var(--page-fg-muted)" }}>
                    Reassign cases to
                  </Label>
                  <Select value={reassignTargetId} onValueChange={setReassignTargetId}>
                    <SelectTrigger data-testid="taxonomy-reassign-target-select">
                      <SelectValue placeholder="Select an active alternate…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {reassignOptions.map((o) => (
                        <SelectItem
                          key={o.id}
                          value={o.id}
                          data-testid={`taxonomy-reassign-target-${o.id}`}
                        >
                          {target?.kind === "issue_type"
                            ? o.name
                            : `${o.issue_type} → ${o.name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {target?.kind === "issue_type" && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider" style={{ color: "var(--page-fg-muted)" }}>
                      Subcategories under this issue type
                    </Label>
                    <div className="mt-1 space-y-1.5">
                      <label className="flex items-center gap-2 text-[12.5px]">
                        <input
                          type="radio"
                          data-testid="taxonomy-children-reparent"
                          name="children-action"
                          checked={childrenAction === "reparent"}
                          onChange={() => setChildrenAction("reparent")}
                        />
                        Move subcategories under the target
                        <span className="text-[11px] text-[color:var(--page-fg-muted)]">
                          (safe default — keeps subcategory names available on future cases)
                        </span>
                      </label>
                      <label className="flex items-center gap-2 text-[12.5px]">
                        <input
                          type="radio"
                          data-testid="taxonomy-children-delete"
                          name="children-action"
                          checked={childrenAction === "delete"}
                          onChange={() => setChildrenAction("delete")}
                        />
                        Delete subcategories after cases are reassigned
                        <span className="text-[11px] text-[color:var(--page-fg-muted)]">
                          (historical cases keep their snapshot)
                        </span>
                      </label>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: "var(--page-fg-muted)" }}>
                    Type the source name to confirm:{" "}
                    <span className="font-mono" style={{ color: "var(--page-fg)" }}>
                      {target?.name}
                    </span>
                  </Label>
                  <Input
                    data-testid="taxonomy-delete-confirm-name"
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={target?.name}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: "var(--page-fg-muted)" }}>
                    Reason (min 10 chars, recorded in audit log)
                  </Label>
                  <Textarea
                    data-testid="taxonomy-delete-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="e.g. Consolidating 'Insubordination' + 'Refusal to follow directive' after policy review."
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && refs && refs.active_case_count === 0 && (
          <div className="space-y-3">
            <div
              className="rounded-md border p-3 text-[12.5px]"
              style={{
                backgroundColor: "var(--danger-bg)",
                borderColor: "var(--danger-border)",
                color: "var(--page-fg)",
              }}
            >
              <p>
                <strong>⚠️ Permanent deletion.</strong> No active cases reference this
                {target?.kind === "issue_type" ? " issue type" : " specific issue"}. Historical
                archived / deleted cases keep their original category snapshot for audit.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider" style={{ color: "var(--page-fg-muted)" }}>
                Type the name to confirm:{" "}
                <span className="font-mono" style={{ color: "var(--page-fg)" }}>
                  {target?.name}
                </span>
              </Label>
              <Input
                data-testid="taxonomy-delete-confirm-name"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={target?.name}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider" style={{ color: "var(--page-fg-muted)" }}>
                Reason for deletion (required, min 10 chars)
              </Label>
              <Textarea
                data-testid="taxonomy-delete-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="e.g. Unused after taxonomy consolidation; superseded by 'Records Retention'."
              />
              <p className="mt-1 text-[10px] text-[color:var(--page-fg-muted)]">
                Recorded in the audit chain alongside the row snapshot.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="taxonomy-delete-cancel"
          >
            {refs && refs.active_case_count > 0 && mode !== "reassign" ? "Close" : "Cancel"}
          </Button>
          {(mode === "delete" || mode === "reassign") && (
            <Button
              onClick={handle}
              disabled={
                saving ||
                (mode === "delete" && !canConfirmDelete) ||
                (mode === "reassign" && !canConfirmReassign)
              }
              data-testid={
                mode === "reassign"
                  ? "taxonomy-reassign-confirm"
                  : "taxonomy-delete-confirm"
              }
              className="bg-[color:var(--danger-fg)] hover:bg-[color:var(--danger-fg)] hover:opacity-90 text-white"
            >
              {saving
                ? "Working…"
                : mode === "reassign"
                  ? `Reassign & delete`
                  : "Delete permanently"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function IssueTypeDialog({ open, onOpenChange, initial, onSave, mode }) {
  const [name, setName] = useState("");
  const [highRisk, setHighRisk] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setHighRisk(!!initial?.high_risk);
    }
  }, [open, initial]);
  const isEdit = mode === "edit";
  const isBuiltin = !!initial?.builtin;
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), high_risk: highRisk });
      onOpenChange(false);
    } catch {
      // toast already surfaced by onSave
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="issue-type-dialog" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit issue type" : "Add issue type"}</DialogTitle>
          <DialogDescription>
            High-risk types auto-flag new cases and trigger the high-risk-assigned
            notification chain.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-[color:var(--page-fg-muted)]">Name</Label>
            <Input
              data-testid="issue-type-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Retention"
              autoFocus
            />
            {isEdit && isBuiltin && (
              <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                This is a system-seeded issue type. Renames are audited but permitted.
              </p>
            )}
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[color:var(--border-subtle)] px-3 py-2">
            <div>
              <p className="text-sm font-medium text-[color:var(--page-fg)]">High-risk</p>
              <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                Auto-flags every new case under this type as high-risk.
              </p>
            </div>
            <Switch
              data-testid="issue-type-highrisk-switch"
              checked={highRisk}
              onCheckedChange={setHighRisk}
              aria-label="Toggle high-risk"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            data-testid="issue-type-save-button"
            onClick={handleSave}
            disabled={saving}
            className="goodwin-primary-btn text-white"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add issue type"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SpecificIssueDialog({ open, onOpenChange, initial, onSave }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setName(initial?.name || "");
  }, [open, initial]);
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      await onSave({ name: name.trim() });
      onOpenChange(false);
    } catch {
      /* toast surfaced by onSave */
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="specific-issue-dialog" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>Rename specific issue</DialogTitle>
          <DialogDescription>
            Parent issue type:{" "}
            <span className="font-medium text-[color:var(--page-fg)]">{initial?.issue_type}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs text-[color:var(--page-fg-muted)]">Name</Label>
          <Input
            data-testid="specific-issue-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            data-testid="specific-issue-save-button"
            onClick={handleSave}
            disabled={saving || name.trim() === (initial?.name || "").trim()}
            className="goodwin-primary-btn text-white"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Taxonomy() {
  const { readOnly } = useOutletContext();
  const [data, setData] = useState({ issue_types: [], specific_issues: [] });
  const [adding, setAdding] = useState({ issue_type: "", name: "" });
  const [busy, setBusy] = useState(false);

  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueDialogMode, setIssueDialogMode] = useState("add");
  const [issueDialogTarget, setIssueDialogTarget] = useState(null);

  const [specDialogOpen, setSpecDialogOpen] = useState(false);
  const [specDialogTarget, setSpecDialogTarget] = useState(null);

  // Phase 2 (Feb 2026) — hard delete + parent move.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [moveBusyId, setMoveBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/taxonomy");
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggleTypeActive = async (t, v) => {
    if (readOnly) return;
    try {
      await api.patch(`/admin/taxonomy/issue-types/${t.id}`, { active: v });
      toast.success(`${t.name} ${v ? "activated" : "deactivated"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleTypeHighRisk = async (t, v) => {
    if (readOnly) return;
    try {
      await api.patch(`/admin/taxonomy/issue-types/${t.id}`, { high_risk: v });
      toast.success(`${t.name} ${v ? "marked" : "unmarked"} as high-risk`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleSpec = async (s, v) => {
    if (readOnly) return;
    try {
      await api.patch(`/admin/taxonomy/specific-issues/${s.id}`, { active: v });
      toast.success(`${s.name} ${v ? "activated" : "deactivated"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const addSpec = async () => {
    if (readOnly || !adding.issue_type || !adding.name.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/taxonomy/specific-issues", {
        issue_type: adding.issue_type,
        name: adding.name.trim(),
        active: true,
      });
      toast.success("Specific issue added");
      setAdding({ issue_type: "", name: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveIssueType = async (payload) => {
    try {
      if (issueDialogMode === "edit" && issueDialogTarget) {
        const body = {};
        // Phase 2 (Feb 2026 · v2) — allow rename of built-in issue types
        // (backend now permits it and audits the change).
        if (payload.name !== issueDialogTarget.name) {
          body.name = payload.name;
        }
        if (!!payload.high_risk !== !!issueDialogTarget.high_risk) {
          body.high_risk = payload.high_risk;
        }
        if (Object.keys(body).length === 0) {
          return;
        }
        await api.patch(`/admin/taxonomy/issue-types/${issueDialogTarget.id}`, body);
        toast.success("Issue type updated");
      } else {
        await api.post("/admin/taxonomy/issue-types", {
          name: payload.name,
          high_risk: payload.high_risk,
          active: true,
        });
        toast.success("Issue type added");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      throw err;
    }
  };

  const saveSpecificIssue = async (payload) => {
    if (!specDialogTarget) return;
    try {
      await api.patch(`/admin/taxonomy/specific-issues/${specDialogTarget.id}`, payload);
      toast.success("Specific issue updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      throw err;
    }
  };

  const openAddIssueType = () => {
    setIssueDialogMode("add");
    setIssueDialogTarget(null);
    setIssueDialogOpen(true);
  };

  const openEditIssueType = (t) => {
    setIssueDialogMode("edit");
    setIssueDialogTarget(t);
    setIssueDialogOpen(true);
  };

  const openEditSpec = (s) => {
    setSpecDialogTarget(s);
    setSpecDialogOpen(true);
  };

  const openDeleteIssueType = (t) => {
    setDeleteTarget({ kind: "issue_type", id: t.id, name: t.name });
    setDeleteDialogOpen(true);
  };

  const openDeleteSpec = (s) => {
    setDeleteTarget({
      kind: "specific_issue",
      id: s.id,
      name: s.name,
      parent_name: s.issue_type,
    });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async (_reason) => {
    if (!deleteTarget) return;
    // The audit trail captures the reason implicitly via the server-side
    // ``before`` snapshot + actor_user_id; we pass reason as an X-header so
    // the append-only audit row includes it without a schema change.
    const path =
      deleteTarget.kind === "issue_type"
        ? `/admin/taxonomy/issue-types/${deleteTarget.id}`
        : `/admin/taxonomy/specific-issues/${deleteTarget.id}`;
    try {
      await api.delete(path, { headers: { "X-Taxonomy-Delete-Reason": _reason } });
      toast.success(
        `${deleteTarget.kind === "issue_type" ? "Issue type" : "Specific issue"} '${deleteTarget.name}' deleted`,
      );
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured "referenced_by_active_cases" is surfaced by the modal
      // itself via the pre-flight GET; this catch handles late races (row
      // acquired an active case between GET references + DELETE).
      if (detail && typeof detail === "object" && detail.error === "referenced_by_active_cases") {
        toast.error(
          `Cannot delete — ${detail.active_case_count} active case(s) reference this now. Refreshing…`,
        );
        load();
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
      throw err;
    }
  };

  const moveSpec = async (s, newParentId, newParentName) => {
    if (readOnly) return;
    setMoveBusyId(s.id);
    try {
      await api.patch(`/admin/taxonomy/specific-issues/${s.id}/move`, {
        new_parent_issue_type_id: newParentId,
      });
      toast.success(`Moved '${s.name}' → '${newParentName}'`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setMoveBusyId(null);
    }
  };

  const confirmReassignAndDelete = async (targetId, reason, childrenAction) => {
    if (!deleteTarget) return;
    const path =
      deleteTarget.kind === "issue_type"
        ? `/admin/taxonomy/issue-types/${deleteTarget.id}/reassign-and-delete`
        : `/admin/taxonomy/specific-issues/${deleteTarget.id}/reassign-and-delete`;
    const body = { target_id: targetId, reason };
    if (deleteTarget.kind === "issue_type") body.children_action = childrenAction;
    try {
      const { data: resp } = await api.post(path, body);
      const targetLabel =
        deleteTarget.kind === "issue_type"
          ? resp.target_name
          : `${resp.target_parent} → ${resp.target_name}`;
      toast.success(
        `Deleted '${resp.source_name}'. Reassigned ${resp.reassigned_case_count} case${
          resp.reassigned_case_count === 1 ? "" : "s"
        } to '${targetLabel}'.`,
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      throw err;
    }
  };

  const groupedSpecs = data.issue_types.map((t) => ({
    type: t,
    items: data.specific_issues.filter((s) => s.issue_type === t.name),
  }));

  return (
    <div data-testid="admin-taxonomy-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Taxonomy"
        title="Issue taxonomy"
        description="Add, rename, deactivate, or delete any issue type or specific issue. Deletion is blocked while active cases reference the row; historical archived / deleted cases keep their original category snapshot. Move a specific issue under a different parent to correct taxonomy drift. High-risk flag auto-marks new cases and triggers the high-risk-assigned notification chain."
      />

      <IssueTypeDialog
        open={issueDialogOpen}
        onOpenChange={setIssueDialogOpen}
        initial={issueDialogTarget}
        onSave={saveIssueType}
        mode={issueDialogMode}
      />
      <SpecificIssueDialog
        open={specDialogOpen}
        onOpenChange={setSpecDialogOpen}
        initial={specDialogTarget}
        onSave={saveSpecificIssue}
      />
      <DeleteTaxonomyDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        target={deleteTarget}
        onConfirm={confirmDelete}
        onReassignAndDelete={confirmReassignAndDelete}
        data={data}
      />

      {!readOnly && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="brand-card flex-1 min-w-[320px] p-4">
            <h2 className="text-sm font-semibold text-[color:var(--page-fg)]">Add specific issue</h2>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <label className="text-xs text-[color:var(--page-fg-muted)]">Issue type</label>
                <Select value={adding.issue_type} onValueChange={(v) => setAdding((a) => ({ ...a, issue_type: v }))}>
                  <SelectTrigger data-testid="taxonomy-add-issue-type" className="w-[200px]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {data.issue_types.filter((t) => t.active).map((t) => (
                      <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                data-testid="taxonomy-add-name-input"
                value={adding.name}
                onChange={(e) => setAdding((a) => ({ ...a, name: e.target.value }))}
                placeholder="Specific issue name"
                className="w-[260px]"
                aria-label="New specific issue name"
              />
              <Button
                data-testid="taxonomy-add-submit"
                onClick={addSpec}
                disabled={busy || !adding.issue_type || !adding.name.trim()}
                className="goodwin-primary-btn text-white"
              >
                <Plus className="mr-1 h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
          <Button
            data-testid="taxonomy-add-issue-type-button"
            onClick={openAddIssueType}
            className="goodwin-primary-btn text-white h-10"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add issue type
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {groupedSpecs.map(({ type, items }) => (
          <details
            key={type.id}
            data-testid={`taxonomy-type-${type.name}`}
            className="brand-card p-4"
            open
          >
            <summary className="flex cursor-pointer items-center justify-between gap-3">
              <span className="flex items-center gap-2 font-semibold text-[color:var(--page-fg)]">
                {type.name}
                <span className="text-xs font-normal text-[color:var(--page-fg-muted)]">
                  ({items.filter((i) => i.active).length} active)
                </span>
                {type.high_risk && (
                  <span
                    data-testid={`taxonomy-type-${type.name}-highrisk-badge`}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                    style={{
                      backgroundColor: "rgba(229,72,77,0.10)",
                      color: "#B42318",
                    }}
                    title="Cases under this type are auto-flagged as high-risk"
                  >
                    <ShieldAlert className="h-3 w-3" strokeWidth={2} />
                    High-risk
                  </span>
                )}
                {type.builtin && (
                  <span className="rounded-full bg-[color:var(--surface-2)] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[color:var(--page-fg-secondary)]">
                    Built-in
                  </span>
                )}
              </span>
              <span className="flex items-center gap-3">
                {!readOnly && (
                  <button
                    type="button"
                    data-testid={`taxonomy-type-${type.name}-edit`}
                    onClick={(e) => {
                      e.preventDefault();
                      openEditIssueType(type);
                    }}
                    className="rounded-md px-2 py-1 text-xs text-[color:var(--page-fg-secondary)] hover:bg-[color:var(--surface-2)]"
                    title={type.builtin ? "Toggle high-risk flag" : "Rename or toggle high-risk"}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    data-testid={`taxonomy-type-${type.name}-delete`}
                    onClick={(e) => {
                      e.preventDefault();
                      openDeleteIssueType(type);
                    }}
                    className="rounded-md px-2 py-1 text-xs text-[color:var(--danger-fg)] hover:bg-[color:var(--danger-bg)]"
                    title="Delete issue type (blocked if active cases reference it)"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">Active</span>
                  <Switch
                    data-testid={`taxonomy-type-${type.name}-active-switch`}
                    checked={!!type.active}
                    onCheckedChange={(v) => toggleTypeActive(type, v)}
                    disabled={readOnly}
                    aria-label={`Toggle ${type.name} active`}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">High-risk</span>
                  <Switch
                    data-testid={`taxonomy-type-${type.name}-highrisk-switch`}
                    checked={!!type.high_risk}
                    onCheckedChange={(v) => toggleTypeHighRisk(type, v)}
                    disabled={readOnly}
                    aria-label={`Toggle ${type.name} high-risk`}
                  />
                </div>
              </span>
            </summary>
            {items.length === 0 ? (
              <p className="mt-3 rounded-md bg-[color:var(--surface-2)] px-3 py-2 text-xs text-[color:var(--page-fg-muted)]">
                No subcategories defined. Cases under this type accept free-text specific issues until you add subcategories above.
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-[color:var(--border-subtle)]">
                {items.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between py-2 text-sm"
                    data-testid={`taxonomy-spec-${s.id}`}
                  >
                    <div>
                      <span className={s.active ? "text-[color:var(--page-fg)]" : "text-[color:var(--page-fg-muted)] line-through"}>
                        {s.name}
                      </span>
                      {s.builtin && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                          built-in
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!readOnly && (
                        <button
                          type="button"
                          data-testid={`taxonomy-spec-${s.id}-edit`}
                          onClick={() => openEditSpec(s)}
                          className="rounded-md px-2 py-1 text-xs text-[color:var(--page-fg-secondary)] hover:bg-[color:var(--surface-2)]"
                          title="Rename specific issue"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {!readOnly && data.issue_types.filter((t) => t.active && t.name !== s.issue_type).length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              data-testid={`taxonomy-spec-${s.id}-move-trigger`}
                              disabled={moveBusyId === s.id}
                              className="rounded-md px-2 py-1 text-xs text-[color:var(--page-fg-secondary)] hover:bg-[color:var(--surface-2)] disabled:opacity-50"
                              title="Move to a different issue type"
                            >
                              <ArrowRightLeft className="h-3.5 w-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="max-h-72 overflow-auto">
                            {data.issue_types
                              .filter((t) => t.active && t.name !== s.issue_type)
                              .map((t) => (
                                <DropdownMenuItem
                                  key={t.id}
                                  data-testid={`taxonomy-spec-${s.id}-move-to-${t.name}`}
                                  onSelect={() => moveSpec(s, t.id, t.name)}
                                  className="text-xs"
                                >
                                  Move to <span className="ml-1 font-semibold">{t.name}</span>
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          data-testid={`taxonomy-spec-${s.id}-delete`}
                          onClick={() => openDeleteSpec(s)}
                          className="rounded-md px-2 py-1 text-xs text-[color:var(--danger-fg)] hover:bg-[color:var(--danger-bg)]"
                          title="Delete specific issue (blocked if active cases reference it)"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <Switch
                        checked={!!s.active}
                        onCheckedChange={(v) => toggleSpec(s, v)}
                        disabled={readOnly}
                        aria-label={`Toggle ${s.name} active`}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </details>
        ))}
      </div>
    </div>
  );
}
